import React, { useState } from 'react';
import { useApplication } from '../../context/ApplicationContext';
import { aiService } from '../../services/aiService';
import RatingVisualization from './RatingVisualization';
import EvaluationDisplay from '../common/EvaluationDisplay';
import useAdminMode from '../../hooks/useAdminMode';
import toast from 'react-hot-toast';
import { getSchoolPrompt } from '../../config/schoolPrompts';

const Step2WorkExperience = ({ schoolPrompt: legacySchoolPrompt }) => {
  const {
    resume,
    selectedSchool,
    demographics,
    academics,
    essays,
    goals,
    additional,
    updateResume,
    setEvaluation,
    setLoading,
    setStep,
    markStepComplete,
    goBack,
    canGoBack,
    resetToSchoolSelection,
    evaluations
  } = useApplication();

  const { isAdminMode, handleTripleClick } = useAdminMode();
  const [resumeText, setResumeText] = useState(resume.content || '');
  const [evaluation, setLocalEvaluation] = useState(null);
  const [showEvaluation, setShowEvaluation] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [showDebugPrompt, setShowDebugPrompt] = useState(false);
  const [lastPromptSent, setLastPromptSent] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!resumeText.trim()) {
      toast.error('Please paste your resume or upload a file');
      return;
    }

    if (resumeText.trim().length < 200) {
      toast.error('Please provide a more detailed resume (at least 200 characters)');
      return;
    }

    // Basic validation for work experience content
    const hasCompanyInfo = /company|employer|corporation|inc|llc|ltd|firm/i.test(resumeText);
    const hasRoleInfo = /manager|analyst|engineer|director|consultant|specialist|coordinator|lead/i.test(resumeText);
    const hasDates = /20\d{2}|19\d{2}|\b\d{1,2}\/\d{4}\b|\b\d{4}\b.*\d{4}\b/i.test(resumeText);
    
    if (!hasCompanyInfo || !hasRoleInfo || !hasDates) {
      toast.error('Please ensure your resume includes company names, job titles, and employment dates');
      return;
    }

    try {
      setLoading(true);
      
      // Update application state
      updateResume({ content: resumeText });

      // Get AI evaluation including Step 1 results - use direct access to avoid state timing issues
      const candidateData = {
        selectedSchool: selectedSchool?.value,
        demographics: demographics,
        academics: academics,
        resume: { content: resumeText },
        essays: essays || {},
        goals: goals || {},
        additional: additional || {}
      };
      
      // Get step 1 evaluation to continue the conversation
      const step1Evaluation = evaluations?.step1 || evaluations?.[`step1`];
      const step1Context = step1Evaluation ? 
        `\n\nSTEP 1 EVALUATION CONTEXT:\n${step1Evaluation.evaluation}\n\nStep 1 Rating: ${step1Evaluation.rating}/5` : 
        '';
      
      // Get step-specific prompt for work experience
      const schoolPrompt = getSchoolPrompt(selectedSchool?.value, 2);
      
      const prompt = `${schoolPrompt}

CANDIDATE INFORMATION:
${JSON.stringify(candidateData, null, 2)}

RESUME CONTENT:
${resumeText}${step1Context}

Please CONTINUE the admission committee conversation from Step 1, now evaluating this candidate's work experience and professional background. Parse the resume carefully - distinguish between company names and skills (e.g., "Microsoft Office" ≠ "worked at Microsoft").`;

      setLastPromptSent(prompt);

      const aiResponse = await aiService.evaluateCandidate(prompt);
      
      if (aiResponse.success) {
        // Process the evaluation to fix formatting
        const processedEvaluation = aiResponse.evaluation
          .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') // Convert **text** to <strong>text</strong>
          .replace(/\*([^*]+)\*/g, '<em>$1</em>'); // Convert *text* to <em>text</em>
        
        const newEvaluation = {
          step: 2,
          stepName: 'Work Experience',
          evaluation: processedEvaluation,
          rating: aiResponse.rating,
          timestamp: new Date().toISOString()
        };
        
        setEvaluation(newEvaluation);
        setLocalEvaluation(newEvaluation);
        setShowEvaluation(true);
      } else {
        toast.error('Failed to get evaluation. Please try again.');
      }
    } catch (error) {
      console.error('Error evaluating work experience:', error);
      toast.error('Error evaluating work experience. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['text/plain', 'application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload a PDF, Word document, or text file');
      return;
    }

    // Validate file size (5MB limit)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      toast.error('File size must be less than 5MB');
      return;
    }

    setIsUploading(true);
    
    try {
      if (file.type === 'application/pdf') {
        // Use PDF.js for proper PDF text extraction
        try {
          // Load PDF.js library dynamically
          if (!window.pdfjsLib) {
            const script = document.createElement('script');
            script.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
            script.onload = () => {
              window.pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
              extractPDFText(file);
            };
            script.onerror = () => {
              toast.error('Failed to load PDF library. Please copy and paste your resume instead.');
              setIsUploading(false);
            };
            document.head.appendChild(script);
          } else {
            extractPDFText(file);
          }
        } catch (error) {
          console.error('PDF processing error:', error);
          toast.error('Error processing PDF. Please copy and paste your resume instead.');
          setIsUploading(false);
        }
      } else {
        // For text and Word documents, read as text
        const reader = new FileReader();
        reader.onload = (e) => {
          const content = e.target.result;
          setResumeText(content);
          toast.success('Resume uploaded successfully!');
          setIsUploading(false);
        };
        reader.onerror = () => {
          toast.error('Error reading file');
          setIsUploading(false);
        };
        reader.readAsText(file);
      }
    } catch (error) {
      console.error('File upload error:', error);
      toast.error('Error uploading file');
      setIsUploading(false);
    }
  };

  const extractPDFText = async (file) => {
    try {
      const arrayBuffer = await file.arrayBuffer();
      const pdf = await window.pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      let fullText = '';

      // Extract text from all pages
      for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        const textContent = await page.getTextContent();
        const textItems = textContent.items.map(item => item.str);
        const pageText = textItems.join(' ');
        fullText += pageText + '\n\n';
      }

      // Clean up the extracted text
      const cleanedText = fullText
        .replace(/\s+/g, ' ') // Multiple spaces to single space
        .replace(/\n\s*\n/g, '\n') // Multiple newlines to single newline
        .trim();

      if (cleanedText.length > 50) {
        setResumeText(cleanedText);
        toast.success('PDF text extracted successfully! Please review and edit as needed.');
      } else {
        toast.error('Could not extract readable text from PDF. Please copy and paste your resume instead.');
      }
      setIsUploading(false);
    } catch (error) {
      console.error('PDF extraction error:', error);
      toast.error('Error extracting text from PDF. Please copy and paste your resume instead.');
      setIsUploading(false);
    }
  };

  const handleProceedToStep3 = () => {
    markStepComplete(2);
    setStep(3);
  };

  const currentEvaluation = evaluations?.step2 || evaluations?.[`step2`] || evaluation;

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="bg-white rounded-lg shadow-lg p-8">
        <div className="flex items-center justify-between mb-6">
          <h2 
            className="text-2xl font-bold text-gray-800"
            onClick={handleTripleClick}
          >
            Step 2: Work Experience & Resume
          </h2>
          <div className="flex gap-2">
            {canGoBack && (
              <button
                onClick={goBack}
                className="px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                ← Back
              </button>
            )}
            <button
              onClick={resetToSchoolSelection}
              className="px-4 py-2 text-red-600 bg-red-50 rounded-lg hover:bg-red-100 transition-colors"
            >
              Start Over
            </button>
          </div>
        </div>

        {/* Debug Mode - Show AI Prompt */}
        {isAdminMode && (
          <div className="mb-6">
            <button
              onClick={() => setShowDebugPrompt(!showDebugPrompt)}
              className="px-4 py-2 bg-yellow-100 text-yellow-800 rounded-lg hover:bg-yellow-200 transition-colors text-sm"
            >
              🔍 {showDebugPrompt ? 'Hide' : 'Show'} AI Prompt
            </button>
            
            {showDebugPrompt && lastPromptSent && (
              <div className="mt-4 p-4 bg-gray-100 rounded-lg">
                <h4 className="font-semibold text-gray-800 mb-2">Prompt Sent to AI:</h4>
                <pre className="text-xs text-gray-700 whitespace-pre-wrap overflow-x-auto max-h-96">
                  {lastPromptSent}
                </pre>
              </div>
            )}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-lg font-medium text-gray-700 mb-3">
              Resume Upload
            </label>
            <p className="text-gray-600 mb-4">
              Please upload your resume or paste its contents below. We'll evaluate your work experience, achievements, and professional background.
            </p>
            
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Upload Resume File (optional)
              </label>
              <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md hover:border-blue-400 transition-colors">
                <div className="space-y-1 text-center">
                  <div className="flex text-sm text-gray-600">
                    <label htmlFor="file-upload" className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500">
                      <span>{isUploading ? 'Uploading...' : 'Upload a file'}</span>
                      <input 
                        id="file-upload" 
                        name="file-upload" 
                        type="file" 
                        className="sr-only"
                        accept=".txt,.doc,.docx,.pdf"
                        onChange={handleFileUpload}
                        disabled={isUploading}
                      />
                    </label>
                    <p className="pl-1">or drag and drop</p>
                  </div>
                  <p className="text-xs text-gray-500">
                    TXT, DOC, DOCX, PDF up to 5MB
                  </p>
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Resume Content
              </label>
              <div className="relative">
                <textarea
                  value={resumeText}
                  onChange={(e) => setResumeText(e.target.value)}
                  placeholder="Paste your complete resume here including:
• Work experience with company names, job titles, dates, and responsibilities
• Education details
• Skills and certifications
• Projects and achievements
• Leadership experience

Be specific about your roles, responsibilities, and accomplishments..."
                  className="w-full h-80 p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-y text-sm"
                  style={{ minHeight: '320px' }}
                />
                <div className="absolute bottom-2 right-2 text-xs text-gray-400">
                  {resumeText.length} characters
                </div>
              </div>
              {resumeText.length > 0 && resumeText.length < 500 && (
                <p className="text-sm text-amber-600 mt-2">
                  ⚠️ Your resume seems quite short. Consider adding more details about your work experience, achievements, and responsibilities.
                </p>
              )}
              {resumeText.length >= 500 && (
                <p className="text-sm text-green-600 mt-2">
                  ✅ Good length! Make sure to include specific achievements and quantifiable results.
                </p>
              )}
            </div>
          </div>

          <div className="flex justify-between items-center">
            <div className="text-sm text-gray-600">
              {resumeText.trim().length >= 200 ? (
                <span className="text-green-600">✅ Resume ready for evaluation</span>
              ) : (
                <span className="text-amber-600">⚠️ Add more details for better evaluation</span>
              )}
            </div>
            <button
              type="submit"
              disabled={!resumeText.trim() || resumeText.trim().length < 200}
              className="px-8 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors font-medium"
            >
              Evaluate Work Experience
            </button>
          </div>
        </form>

        {showEvaluation && currentEvaluation && (
          <div className="mt-8">
            <div className="mb-6">
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                Work Experience Evaluation
              </h3>
              <div className="mb-4">
                <RatingVisualization rating={currentEvaluation?.rating} />
              </div>
            </div>
            
            <EvaluationDisplay 
              evaluation={currentEvaluation?.evaluation}
              rating={currentEvaluation?.rating}
            />


            
            <div className="mt-6 flex justify-between">
              <button
                onClick={goBack}
                disabled={!canGoBack()}
                className="px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
              >
                ← Back to Demographics
              </button>
              <button
                onClick={handleProceedToStep3}
                className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                Continue to Essays →
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Step2WorkExperience; 